import { create } from 'zustand'

export interface FileNode {
  name: string
  path: string
  type: 'file' | 'folder'
  content?: string
  children?: FileNode[]
  isOpen?: boolean
  id?: number  // Backend file ID for versioning
  currentVersion?: number
  versionCount?: number
}

interface FileStore {
  files: FileNode[]
  activeFile: string | null
  openFiles: string[]
  setFiles: (files: FileNode[]) => void
  addFile: (path: string, content?: string, metadata?: Partial<FileNode>) => void
  addFolder: (path: string) => void
  updateFileContent: (path: string, content: string) => void
  updateFileMetadata: (path: string, metadata: Partial<FileNode>) => void
  deleteFile: (path: string) => void
  renameFile: (oldPath: string, newPath: string) => void
  setActiveFile: (path: string | null) => void
  openFile: (path: string) => void
  closeFile: (path: string) => void
  toggleFolder: (path: string) => void
  getFileContent: (path: string) => string | undefined
  getFileNode: (path: string) => FileNode | null
  importFiles: (fileList: FileList) => Promise<void>
}

const findNode = (nodes: FileNode[], path: string): FileNode | null => {
  for (const node of nodes) {
    if (node.path === path) return node
    if (node.children) {
      const found = findNode(node.children, path)
      if (found) return found
    }
  }
  return null
}

const insertNode = (nodes: FileNode[], pathParts: string[], node: FileNode, currentPath = ''): FileNode[] => {
  if (pathParts.length === 1) {
    const existingIndex = nodes.findIndex(n => n.name === node.name)
    if (existingIndex >= 0) {
      nodes[existingIndex] = node
    } else {
      nodes.push(node)
    }
    nodes.sort((a, b) => {
      if (a.type === b.type) return a.name.localeCompare(b.name)
      return a.type === 'folder' ? -1 : 1
    })
    return [...nodes]
  }

  const folderName = pathParts[0]
  const folderPath = currentPath ? `${currentPath}/${folderName}` : folderName
  let folder = nodes.find(n => n.name === folderName && n.type === 'folder')
  
  if (!folder) {
    folder = { name: folderName, path: folderPath, type: 'folder', children: [], isOpen: true }
    nodes.push(folder)
    nodes.sort((a, b) => {
      if (a.type === b.type) return a.name.localeCompare(b.name)
      return a.type === 'folder' ? -1 : 1
    })
  }

  folder.children = insertNode(folder.children || [], pathParts.slice(1), node, folderPath)
  return [...nodes]
}

const removeNode = (nodes: FileNode[], path: string): FileNode[] => {
  return nodes
    .filter(node => node.path !== path)
    .map(node => ({
      ...node,
      children: node.children ? removeNode(node.children, path) : undefined
    }))
}

export const useFileStore = create<FileStore>((set, get) => ({
  files: [],
  activeFile: null,
  openFiles: [],

  setFiles: (files) => set({ files }),

  addFile: (path, content = '', metadata = {}) => {
    const pathParts = path.split('/')
    const fileName = pathParts[pathParts.length - 1]
    const newFile: FileNode = {
      name: fileName,
      path,
      type: 'file',
      content,
      ...metadata
    }
    set(state => ({
      files: insertNode([...state.files], pathParts, newFile)
    }))
  },

  addFolder: (path) => {
    const pathParts = path.split('/')
    const folderName = pathParts[pathParts.length - 1]
    const newFolder: FileNode = {
      name: folderName,
      path,
      type: 'folder',
      children: [],
      isOpen: true
    }
    set(state => ({
      files: insertNode([...state.files], pathParts, newFolder)
    }))
  },

  updateFileContent: (path, content) => {
    const updateContent = (nodes: FileNode[]): FileNode[] => {
      return nodes.map(node => {
        if (node.path === path) {
          return { ...node, content }
        }
        if (node.children) {
          return { ...node, children: updateContent(node.children) }
        }
        return node
      })
    }
    set(state => ({ files: updateContent(state.files) }))
  },

  updateFileMetadata: (path, metadata) => {
    const updateMetadata = (nodes: FileNode[]): FileNode[] => {
      return nodes.map(node => {
        if (node.path === path) {
          return { ...node, ...metadata }
        }
        if (node.children) {
          return { ...node, children: updateMetadata(node.children) }
        }
        return node
      })
    }
    set(state => ({ files: updateMetadata(state.files) }))
  },

  deleteFile: (path) => {
    set(state => ({
      files: removeNode(state.files, path),
      openFiles: state.openFiles.filter(f => f !== path && !f.startsWith(path + '/')),
      activeFile: state.activeFile === path || state.activeFile?.startsWith(path + '/') 
        ? state.openFiles.filter(f => f !== path)[0] || null 
        : state.activeFile
    }))
  },

  renameFile: (oldPath, newPath) => {
    const renameInTree = (nodes: FileNode[]): FileNode[] => {
      return nodes.map(node => {
        if (node.path === oldPath) {
          const newName = newPath.split('/').pop() || ''
          return { ...node, name: newName, path: newPath }
        }
        if (node.children) {
          return { ...node, children: renameInTree(node.children) }
        }
        return node
      })
    }
    set(state => ({
      files: renameInTree(state.files),
      openFiles: state.openFiles.map(f => f === oldPath ? newPath : f),
      activeFile: state.activeFile === oldPath ? newPath : state.activeFile
    }))
  },

  setActiveFile: (path) => set({ activeFile: path }),

  openFile: (path) => {
    set(state => {
      if (!state.openFiles.includes(path)) {
        return { openFiles: [...state.openFiles, path], activeFile: path }
      }
      return { activeFile: path }
    })
  },

  closeFile: (path) => {
    set(state => {
      const newOpenFiles = state.openFiles.filter(f => f !== path)
      let newActiveFile = state.activeFile
      if (state.activeFile === path) {
        const index = state.openFiles.indexOf(path)
        newActiveFile = newOpenFiles[Math.min(index, newOpenFiles.length - 1)] || null
      }
      return { openFiles: newOpenFiles, activeFile: newActiveFile }
    })
  },

  toggleFolder: (path) => {
    const toggleInTree = (nodes: FileNode[]): FileNode[] => {
      return nodes.map(node => {
        if (node.path === path && node.type === 'folder') {
          return { ...node, isOpen: !node.isOpen }
        }
        if (node.children) {
          return { ...node, children: toggleInTree(node.children) }
        }
        return node
      })
    }
    set(state => ({ files: toggleInTree(state.files) }))
  },

  getFileContent: (path) => {
    const node = findNode(get().files, path)
    return node?.content
  },

  getFileNode: (path) => {
    return findNode(get().files, path)
  },

  importFiles: async (fileList) => {
    const readFile = (file: File): Promise<{ path: string; content: string }> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          resolve({
            path: file.webkitRelativePath || file.name,
            content: reader.result as string
          })
        }
        reader.onerror = reject
        reader.readAsText(file)
      })
    }

    const results = await Promise.all(
      Array.from(fileList).map(file => readFile(file).catch(() => null))
    )

    results.filter(Boolean).forEach(result => {
      if (result) {
        get().addFile(result.path, result.content)
      }
    })
  }
}))
